import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";

interface EventFormProps {
  userId: string;
}

const EventForm = ({ userId }: EventFormProps) => {
  const [loading, setLoading] = useState(false);
  const [flyerFile, setFlyerFile] = useState<File | null>(null);
  const [flyerPreview, setFlyerPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    title: string;
    date: string;
    start_time: string;
    end_time: string;
    location: string;
    description: string;
    external_link: string;
    chapter: "washington" | "union" | "all";
  }>({
    title: "",
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    description: "",
    external_link: "",
    chapter: "all",
  });

  const handleFlyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setFlyerFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFlyerPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeFlyerPreview = () => {
    setFlyerFile(null);
    setFlyerPreview(null);
  };

  const uploadFlyer = async (): Promise<string | null> => {
    if (!flyerFile) return null;

    const fileExt = flyerFile.name.split(".").pop();
    const filePath = `${userId}/${Date.now()}.${fileExt}`;

    const { error } = await supabase.storage
      .from("event-flyers")
      .upload(filePath, flyerFile);

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from("event-flyers")
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const imageUrl = await uploadFlyer();

      const { error } = await supabase.from("events").insert([
        {
          created_by: userId,
          title: formData.title,
          type: "Special Event",
          date: formData.date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          location: formData.location,
          description: formData.description,
          external_link: formData.external_link || null,
          is_special: true,
          chapter: formData.chapter,
          image_url: imageUrl,
        },
      ]);

      if (error) throw error;

      toast.success("Event created successfully!");
      setFormData({
        title: "",
        date: "",
        start_time: "",
        end_time: "",
        location: "",
        description: "",
        external_link: "",
        chapter: "all",
      });
      setFlyerFile(null);
      setFlyerPreview(null);
    } catch (error: any) {
      toast.error("Error creating event: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Special Event</CardTitle>
        <CardDescription>
          Add a new event to the RCN calendar
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Event Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chapter">Chapter *</Label>
            <Select
              value={formData.chapter}
              onValueChange={(value) => handleChange("chapter", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select chapter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Chapters (Shared Event)</SelectItem>
                <SelectItem value="washington">Washington Chapter Only</SelectItem>
                <SelectItem value="union">Union Chapter Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange("date", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="start_time">Start Time *</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => handleChange("start_time", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_time">End Time *</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChange("end_time", e.target.value)}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => handleChange("location", e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              required
            />
          </div>

          {/* Flyer Upload */}
          <div className="space-y-2">
            <Label>Event Flyer (Optional)</Label>
            {flyerPreview ? (
              <div className="relative inline-block">
                <img
                  src={flyerPreview}
                  alt="Flyer preview"
                  className="max-h-64 rounded-lg border object-contain"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={removeFlyerPreview}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label
                htmlFor="flyer-upload"
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 cursor-pointer hover:border-primary/50 transition-colors"
              >
                <ImagePlus className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload a flyer image
                </span>
                <input
                  id="flyer-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFlyerChange}
                />
              </label>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="external_link">External Link (Optional)</Label>
            <Input
              id="external_link"
              type="url"
              value={formData.external_link}
              onChange={(e) => handleChange("external_link", e.target.value)}
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Event"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default EventForm;
